import { t } from "@lingui/core/macro";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppState, Platform } from "react-native";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { HStack, Text } from "@whoosh/design-system/src/components/primitive";
import { useSlotLockCountdownTimerData } from "@whoosh/utilities/src/hooks";

import {
  useCanUseNativeSession,
  useNativeSessionReady,
} from "../../../BiometricAuthentication/NativeSessionGateContext";
import { getServerTimeOffsetMs } from "../../../Common/utils/serverTimeOffset";
import { LockedSlots_atom } from "../../atoms";
import { SlotLockSubscriptionManager } from "../SlotLockSubscriptionManager/SlotLockSubscriptionManager";

type Props = {
  agendaId?: string | null;
  lockedUntil: string | null;
  currentServerTime?: string | null;
  timeSlotId?: string | null;
  onExpireCallback?: () => void;
};
export const SlotLockCountdownTimer = ({
  agendaId,
  lockedUntil,
  currentServerTime,
  timeSlotId,
  onExpireCallback,
}: Props) => {
  const canUseNativeSession = useCanUseNativeSession();
  const sessionReady = useNativeSessionReady();
  const updateLockedSlotInfo = useSetAtom(LockedSlots_atom);
  const { showToast } = useToast();
  const handledLockEndRef = useRef(false);
  const pendingExpiryRef = useRef(false);
  // Another Relay subscription can clear lockedUntil before the staff-ended
  // event arrives. Keep listening for the lock we observed until it is handled.
  const [observedLock, setObservedLock] = useState({ timeSlotId, lockedUntil });
  if (
    observedLock.timeSlotId !== timeSlotId ||
    (lockedUntil && observedLock.lockedUntil !== lockedUntil)
  ) {
    setObservedLock({ timeSlotId, lockedUntil });
  }
  const serverTimeOffsetMs = useMemo(
    () => getServerTimeOffsetMs(currentServerTime),
    [currentServerTime]
  );

  useEffect(() => {
    handledLockEndRef.current = false;
    pendingExpiryRef.current = false;
  }, [observedLock]);

  const handleLockEnded = useCallback(() => {
    if (handledLockEndRef.current) return;
    pendingExpiryRef.current = true;
    if (
      !sessionReady ||
      !canUseNativeSession() ||
      (Platform.OS !== "web" && AppState.currentState !== "active")
    ) {
      return;
    }

    handledLockEndRef.current = true;
    pendingExpiryRef.current = false;
    updateLockedSlotInfo(null);
    showToast({
      title: t`Timer Expired`,
      description: t`Whoops! Looks like the timer ran out and we lost your booking lock.`,
      semanticStyle: "error",
      toastId: "tee-time-unlocked",
    });
    onExpireCallback?.();
  }, [
    canUseNativeSession,
    sessionReady,
    onExpireCallback,
    showToast,
    updateLockedSlotInfo,
  ]);

  useEffect(() => {
    if (pendingExpiryRef.current) handleLockEnded();
    const subscription =
      Platform.OS !== "web"
        ? AppState.addEventListener("change", (state) => {
            if (state === "active" && pendingExpiryRef.current)
              handleLockEnded();
          })
        : null;
    return () => {
      subscription?.remove();
    };
  }, [handleLockEnded]);

  const { timeWarning, timeLow } = useSlotLockCountdownTimerData({
    lockedUntil,
    onExpire: handleLockEnded,
    serverTimeOffsetMs,
  });

  return (
    <>
      {agendaId && timeSlotId && observedLock.lockedUntil ? (
        <SlotLockSubscriptionManager
          agendaId={agendaId}
          lockedUntil={observedLock.lockedUntil}
          timeSlotId={timeSlotId}
          onLockEnded={handleLockEnded}
        />
      ) : null}
      {timeWarning ? (
        <HStack>
          <Text variant={"mainSmallSemiBold"}>{t`Reserve in the next:`}</Text>
          <Text
            variant={"mainSmallSemiBold"}
            color={timeLow ? "text-error" : "text-main-primary"}
            testID="slot-lock-countdown-timer"
          >
            {" "}
            {timeWarning}
          </Text>
        </HStack>
      ) : null}
    </>
  );
};
